// © Microsoft Corporation. All rights reserved.

import { pageRevealStartMarker, timeToVideoStart, timeToVisuallyReady } from "./PerfMarkerConstants";

import { canUseDOM } from "@msnews/isomorphic-util";
import { getPageTimings } from "./PageTimings";
import { isNullOrUndefined } from "util";
import { perfMarker } from "./PerfMarker";
import { rafTimer } from "./RAFTimer";

/** Collection of marker names grouped by marker type */
export interface MarkerNames {
    /** Array of timeToFunctional names */
    TTF?: string[];

    /** Array of timeToVisualReady names */
    TTVR?: string[];

    /** Array of timeToPageReveal names */
    TTPR?: string[];
}

/** Enum for PerfMarkersGroup */
export enum PerfMarkersGroup {
    Primary = "primary",
    Secondary = "secondary"
}

type AggregateMetadata = {
    name: string;
    preserve: boolean;
};

let remainingMarkers: Set<string>;
let remainingSecondaryMarkers: Set<string>;
let remainingTtvrMarkers: Set<string>;
let remainingTtprMarkers: Set<string>;
let ttvrMarkers: Set<string>;
let ttvrOldMarkers: Set<string>;
/** Map the marker ID to the Aggregate value. Key is the Marker (TTVR.WeatherWC), value is to where they'll be aggregated  (TTVR.SDCards)*/
const aggregateMapper: Map<string, AggregateMetadata> = new Map();
/** Track the Aggreated TTVR Value as the scalar Markers are updated */
const ttvrAggMarkers: Map<string, number> = new Map();

let ttvrPromise: Promise<[string, number][]>;
let resolveTtvrCallback: (markers: [string, number][]) => void;

let perfMarkerPromise: Promise<[string, number][]>;
let perfMarkerResolveCallback: (markers: [string, number][]) => void;
let secondaryPerfMarkerPromise: Promise<[string, number][]>;
let secondaryPerfMarkerResolveCallback: (markers: [string, number][]) => void;

let ttvsMarkerPromise: Promise<[string, number][]>;
let resolveTtvsCallback: (ttvs: [string, number][]) => void;

let ttprMarkerPromise: Promise<[string, number][]>;
let resolveTtprCallback: (ttpr: [string, number][]) => void;

/** The markers map. Key is the experience name, e.g. "River". Value is the number of milliseconds it took to reach that marker. */
let primaryMarkers: Map<string, number> = new Map();

/** The secondary markers map. Key is the experience name, e.g. "TTS.NativeAd". Value is the number of milliseconds it took to reach that marker. */
let secondaryMarkers: Map<string, number> = new Map();

const ttfPrefix = "TTF.";
const ttvrPrefix = "TTVR.";
const ttvsPrefix = "TTVS.";
const ttprPrefix = "TTPR.";
const ttslPrefix = "TTSL.";

let isSetMarkersCalled = false;
let isNewTtvr = false;

/**
 * Global override flga to indicate whether to calculate the final
 * `TTVR` page timing value based on individual TTVRs. This should be disabled
 * via `disablePageTTVR` helper on pages that do not desire `TTVR` to be present.
 * (For example bing homepage where PLTV2 is used as the overall page metric)
 */
let markPageTTVROverride: boolean;

/**
 * Helper to disable setting the overall "TTVR" marker for scenarios where
 * the overall page marker is set by some custom host page logic instead of via
 * individual Peregrine experience TTVRs.
 */
export function disablePageTTVR(): void {
    markPageTTVROverride = false;
}

/**
 * Saves the Time To Functional timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTF to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtf(name = "", markTimeNA = false, time?: number): void {
    if (canUseDOM()) {
        time = time || window.performance.now();
        return setMark(ttfPrefix, name, markTimeNA, time);
    }
}

/**
 * Saves the Time To Visually Ready timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTVR to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtvr(name = "", markTimeNA = false, time?: number): void {
    if (canUseDOM()) {
        time = time || window.performance.now();
        return setMark(ttvrPrefix, name, markTimeNA, time);
    }
}

/**
 * Helper that wraps the markTtvr call inside a request animation frame and setTimeout
 * with the idea that this will allow the experience time to render before setting the mark
 * @param name The marker name
 * @param markTimeNA To set -1 mark time for the TTVR to resolve the dependency
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtvrRaf(name = "", markTimeNA = false, time?: number): void {
    rafTimer(() =>
        markTtvr(name, markTimeNA, time)
    );
}

/**
 * Saves the Time To Page Reveal timestamp for the given marker.
 * The call is inside a request animation frame and setTimeout,
 * with the idea that this will allow the experience time to render before setting the mark.
 * @param name The marker name
 */
export function markTtprRaf(name = ""): void {
    rafTimer(() => {
        const startMarker = getSinglePerfMarkStartTimeByName(pageRevealStartMarker);
        const time = startMarker === -1
            ? -1
            : performance.now() - startMarker;

        setMark(ttprPrefix, name, false, time);
    });

    if (!ttprMarkerPromise) {
        // add the promise
        waitForTtprMarkers();
    }
}

/**
 * Saves the Time to Video Start timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTVR to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtvs(name = "", markTimeNA = false, time?: number): void {
    if (canUseDOM()) {
        time = time || window.performance.now();
        return setMark(ttvsPrefix, name, markTimeNA, time);
    }
}

/**
 * Saves the Time To Start Loading timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the TTVR to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function markTtsl(name = "", markTimeNA = false, time?: number): void {
    if (canUseDOM()) {
        time = time || window.performance.now();
        return setMark(ttslPrefix, name, markTimeNA, time);
    }
}

/**
 * Saves the time stamp for a given secondary perf marker.
 * @param markerName - The marker name
 * @param time - Time in milliseconds
 */
export function setSecondaryPerfMarkerTime(markerName: string, time?: number) {
    // return if the marker already exists.
    if (secondaryMarkers.get(markerName)) {
        return;
    }

    const existingMarker = performance.getEntriesByName && performance.getEntriesByName(markerName);
    const isMarkerSet = Boolean(existingMarker && existingMarker.length);

    if (!isMarkerSet) {
        perfMarker.singleMark(markerName);

        if (isNullOrUndefined(time)) {
            secondaryMarkers.set(markerName, -1);
        } else {
            secondaryMarkers.set(markerName, Math.round(time));
        }
    } else {
        secondaryMarkers.set(markerName, Math.round(existingMarker[0].startTime));
    }

    // Notify any callers waiting for reports
    if (remainingSecondaryMarkers && remainingSecondaryMarkers.delete(markerName) && remainingSecondaryMarkers.size === 0 && secondaryPerfMarkerResolveCallback) {
        secondaryPerfMarkerResolveCallback(getSecondaryMarkers());
    }
}

/**
 * Saves the timestamp for the given marker and also marks the performance timeline.
 * If the same marker is added again, it overrides with the original marker.
 * @param name - The marker name
 * @param markTimeNA - To set -1 mark time for the ttvr to resolve the dependency.
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
function setMark(prefix: string, name: string, markTimeNA: boolean, time: number): void {
    if (!(name && name.length)) {
        return;
    }

    if (!(prefix && prefix.length)) {
        return;
    }

    const markerFullName = prefix + name;

    // To check if the marker exists then return.
    if (primaryMarkers.get(markerFullName)) {
        return;
    }

    const existingMarker = performance.getEntriesByName && performance.getEntriesByName(markerFullName);
    const isMarkerSet = Boolean(existingMarker && existingMarker.length);

    if (!isMarkerSet) {
        perfMarker.singleMark(markerFullName);

        if (markTimeNA) {
            primaryMarkers.set(markerFullName, -1);
        } else {
            primaryMarkers.set(markerFullName, Math.round(time));
        }
    } else {
        primaryMarkers.set(markerFullName, Math.round(existingMarker[0].startTime));
    }

    // Notify any callers waiting for TTVR reports
    if (remainingTtvrMarkers && remainingTtvrMarkers.delete(markerFullName) && remainingTtvrMarkers.size === 0 && resolveTtvrCallback) {
        resolveTtvrCallback(getTtvrMarkers());
    }

    // Notify any callers waiting for reports
    if (remainingMarkers && remainingMarkers.delete(markerFullName) && remainingMarkers.size === 0 && perfMarkerResolveCallback) {
        perfMarkerResolveCallback(getMarkers());
    }

    // Resolve TTVS if this is ttvs. TTVS only need one time to mark instead of multiple markers.
    if (prefix === ttvsPrefix && resolveTtvsCallback) {
        resolveTtvsCallback(getTtvsMarkers());
    }

    // Notify any callers waiting for TTPR reports
    if (prefix === ttprPrefix
        && remainingTtprMarkers
        && remainingTtprMarkers.delete(markerFullName)
        && remainingTtprMarkers.size === 0 && resolveTtprCallback) {
        resolveTtprCallback(getMarkers());
    }
}

/**
 * Sets the collection of primary perf markers to use for waitForAllPerfMarkers, waitForTtvr .
 * @param markerNames The collection of primary TTF and TTVR perf markers.
 */
export function setMarkers(markerNames: MarkerNames): void {
    remainingMarkers = getRemainingMarkers(markerNames);
    remainingTtvrMarkers = getRemainingMarkers(markerNames, "TTVR");
    remainingTtprMarkers = getRemainingMarkers(markerNames, "TTPR");
    const flatMarkerNames = flattenMarkerNames(markerNames, "TTVR");
    ttvrMarkers = new Set<string>(flatMarkerNames);
    // These are the old marker names for the TTVR calculation prior to FY22
    ttvrOldMarkers = new Set<string>(flatMarkerNames);

    if (resolveTtvrCallback && remainingTtvrMarkers.size === 0) {
        perfMarkerResolveCallback(getTtvrMarkers());
    }

    if (perfMarkerResolveCallback && remainingMarkers.size === 0) {
        perfMarkerResolveCallback(getMarkers());
    }

    isSetMarkersCalled = true;
}

/**
 * Sets the collection of secondary perf markers to use for waitForAllSecondaryPerfMarkers. Should be called only once.
 * @param markerNames The collection of secondary perf markers that are required.
 */
export function setRequiredSecondaryMarkers(markerNames: string[]): void {
    // Return if remainingSecondaryMarkers is already set. This means setRequiredSecondaryMarkers was already called.
    if (remainingSecondaryMarkers) {
        return;
    }

    remainingSecondaryMarkers = new Set<string>();

    markerNames.forEach(markerName => {
        if (!secondaryMarkers.get(markerName)) {
            remainingSecondaryMarkers.add(markerName);
        }
    });

    // If there are no required secondary perf markers, immediately resolve the promise
    if (remainingSecondaryMarkers.size === 0 && secondaryPerfMarkerResolveCallback) {
        secondaryPerfMarkerResolveCallback(getSecondaryMarkers());
    }
}

/**
 * Determines whether setMarkers has been called.
 */
export function setMarkersCalled(): boolean {
    return isSetMarkersCalled;
}

/**
 * Adds marker to the remainingTtvrMarkers
 * Here for the case where the marker cannot be added in experience tracker
 * Example: Stripe layout - since we don't know if user has that layout selected
 * until later (in layoutPreferenceData connector)
 *
 * @export
 * @param {string} markerName
 * @returns {void}
 */
export function addTtvrMarker(markerName: string): void {
    if (!markerName || !isSetMarkersCalled || !remainingMarkers || !remainingTtvrMarkers || !isNewTtvr) {
        return;
    }
    if (!markerName.startsWith(ttvrPrefix)) {
        markerName = ttvrPrefix + markerName;
    }
    remainingMarkers.add(markerName);
    remainingTtvrMarkers.add(markerName);
    ttvrMarkers.add(markerName);
}

/**
 * Waits for the all perf markers supplied to setMarkers() to be logged.
 * @returns A promise with all of the TTF and TTVR perf markers, including the overall page marker
 */
export function waitForAllPerfMarkers(): Promise<[string, number][]> {

    // Perf marker promises not supported on server side.
    if (!canUseDOM()) {
        return null;
    }

    return waitForPerfMarkers();
}

/**
 * Waits for the all perf markers supplied to setMarkers() to be logged.
 * @returns A promise with all of the TTF and TTVR perf markers, including the overall page marker
 */
export function waitForAllSecondaryPerfMarkers(): Promise<[string, number][]> {

    // Perf marker promises not supported on server side.
    if (!canUseDOM()) {
        return null;
    }

    // If we have a promise, return it. It may already be completed.
    if (secondaryPerfMarkerPromise) {
        return secondaryPerfMarkerPromise;
    }

    if (!remainingSecondaryMarkers || remainingSecondaryMarkers.size === 0) {
        return Promise.resolve(getSecondaryMarkers());
    }

    // If the markers are not yet declared or are incomplete, set up a promise to resolve when they are all done.
    secondaryPerfMarkerPromise = new Promise<[string, number][]>(resolve => {
        secondaryPerfMarkerResolveCallback = resolve;
    });

    return secondaryPerfMarkerPromise;
}

/**
 * Waits for the TTVR markers supplied to setMarkers() to be logged.
 * @returns A promise with the TTVR perf markers, including the overall page TTVR marker
 */
export function waitForTtvr(): Promise<[string, number][]> {

    // Perf marker promises not supported on server side.
    if (!canUseDOM()) {
        return null;
    }

    // If we have a promise, return it. It may already be completed.
    if (ttvrPromise) {
        return ttvrPromise;
    }

    if (remainingTtvrMarkers && remainingTtvrMarkers.size === 0) {
        return Promise.resolve(getTtvrMarkers());
    }

    // If the markers are not yet declared or are incomplete, set up a promise to resolve when they are all done.
    ttvrPromise = new Promise<[string, number][]>(resolve => {
        resolveTtvrCallback = resolve;
    });

    return ttvrPromise;
}

/**
 * Waits for the perf markers supplied to setMarkers() to be logged.
 * @returns A promise with all of the TTF and TTVR perf markers, including the overall page marker
 */
function waitForPerfMarkers(): Promise<[string, number][]> {

    // Perf marker promises not supported on server side.
    if (!canUseDOM()) {
        return null;
    }

    // If we have a promise, return it. It may already be completed.
    if (perfMarkerPromise) {
        return perfMarkerPromise;
    }

    if (remainingMarkers && remainingMarkers.size === 0) {
        return Promise.resolve(getMarkers());
    }

    // If the markers are not yet declared or are incomplete, set up a promise to resolve when they are all done.
    perfMarkerPromise = new Promise<[string, number][]>(resolve => {
        perfMarkerResolveCallback = resolve;
    });

    return perfMarkerPromise;
}

/**
 * Wait for the ttvs marker to be logged
 * @returns A promise with TTVS perf time in ms
 */
export function waitForTtvsMarker(): Promise<[string, number][]> {

    // Perf marker promises not supported on server side.
    if (!canUseDOM()) {
        return null;
    }

    if (ttvsMarkerPromise) {
        return ttvsMarkerPromise;
    }

    ttvsMarkerPromise = new Promise<[string, number][]>(resolve => {
        resolveTtvsCallback = resolve;
    });

    return ttvsMarkerPromise;
}

/**
 * Wait for the ttpr marker to be logged
 * @returns A promise with TTPR perf time in ms
 */
export function waitForTtprMarkers(): Promise<[string, number][]> {

    // Perf marker promises not supported on server side.
    if (!canUseDOM()) {
        return null;
    }

    if (ttprMarkerPromise) {
        return ttprMarkerPromise;
    }

    ttprMarkerPromise = new Promise<[string, number][]>(resolve => {
        resolveTtprCallback = resolve;
    });

    return ttprMarkerPromise;
}

/**
 * Returns TTVR of given entry if exists in markers array
 * @param entryName the entry name for the marker eg. "TTVR.xx" or just "TTVR"
 * @returns the TTVR value for that entry name OR undefined if entry does not exist
 */
export function getTtvrOfEntry(entryName: string): number {
    const ttvrMarkers = getTtvrMarkers();
    const marker = ttvrMarkers.find((mark) => mark[0] === entryName) || [];

    return marker[1];
}

/**
 * Returns array of markers, including the page-level marker. For example:
 * Note: didn't add TTVS in getMarkers because TTVS only applies to specific page type and special cases.
 * It could be fired way after in the page cycle.
 * [["TTVR.Search", 100], ["TTVR.TopSites", 100.123], ["TTVR.River", 100.504], ["TTVR", 100.504]]
 * @returns The array of markers
 */
export function getMarkers(): [string, number][] {
    const ttvrResults = getTtvrMarkers();
    const ttfResults = getTtfMarkers();
    const result: [string, number][] = [...ttvrResults, ...ttfResults];

    return result;
}

/**
 * Returns array of markers as they are available. Do not write page level markers here.
 * This is used when waiting for remaining markers timed out.
 * [["TTVR.Search", 100], ["TTVR.TopSites", 100.123]]
 * @returns The array of markers
 */
export function getAvailableMarkers(): [string, number][] {
    // Do not mark page TTVR as not all remaining markers have been satisfied.
    const ttvrResults = getTtvrMarkers(false);
    const ttfResults = getTtfMarkers();
    const result: [string, number][] = [...ttvrResults, ...ttfResults];

    return result;
}

/**
 * Returns array of secondary markers. E.g.:
 * [["TTS.NativeAd", 3300], ["TTS.SearchHistory", 2100]]
 * @returns The array of secondary markers
 */
export function getSecondaryMarkers(): [string, number][] {
    const result: [string, number][] = [];

    secondaryMarkers.forEach((value: number, key: string) => {
        result.push([key, value]);
    });

    return result;
}

/**
 * Returns array of markers that are yet to be completed.
 * @returns The array of incomplete markers
 */
export function getIncompleteMarkers(perfMarkersGroup: PerfMarkersGroup): string[] {
    return perfMarkersGroup === PerfMarkersGroup.Primary
        ? Array.from(remainingMarkers || [])
        : Array.from(remainingSecondaryMarkers || []);
}

/**
 * Returns markers yet to be set
 * @param markerNames The names of the markers to check. e.g. { TTVR: ["River", "TopSites"], TTF: ["SearchBox"] }
 * @param markerType The type of markers to return "TTVR", "TTF", or if undefined all
 * @returns The set of markers that have not yet been set.
 */
function getRemainingMarkers(markerNames: MarkerNames, markerType?: string): Set<string> {
    const result: Set<string> = new Set<string>();
    for (const markerName of flattenMarkerNames(markerNames, markerType)) {
        if (!primaryMarkers.get(markerName)) {
            result.add(markerName);
        }
    }

    return result;
}

/**
* Returns TTPR of given entry if exists in markers array
* @param expType - key for experience
* @returns the TTVR value for that entry name OR undefined if entry does not exist
*/
export function getExperienceTtpr(expType: string): number {
    const ttprMarkers = getTtprMarkers();
    const marker = ttprMarkers.find((mark) => mark[0].includes(expType)) || [];

    return marker[1];
}

/**
* Returns array of TTPR markers, time to page reveal. For example:
* [["TTPR.SearchBoxEdgeNext", 120], ["BackgroundImageEdgeNext", 120]]
* @returns The array of markers
*/
export function getTtprMarkers(): [string, number][] {
    return getMarkersWithType(ttprPrefix);
}

/**
 * Returns array of TTF markers, including the page-level marker. For example:
 * [["TTF.Search", 100], ["TTF", 100]]
 * @returns The array of markers
 */
function getTtfMarkers(): [string, number][] {
    return getMarkersWithType(ttfPrefix);
}

/**
 * Returns array of TTVR markers, including the page-level marker. For example:
 * [["TTVR.Search", 100], ["TTVR.TopSites", 100.123], ["TTVR.River", 100.504], ["TTVR", 100.504]]
 * @param markPageTTVR flag to mark overall TTVR marker based on max TTVR markers
 * @returns The array of markers
 */
function getTtvrMarkers(markPageTTVR = true): [string, number][] {
    return markPageTTVR && (markPageTTVROverride !== false)
        ? getMarkersWithType(ttvrPrefix, timeToVisuallyReady, true)
        : getMarkersWithType(ttvrPrefix);
}

/**
 * Returns array of TTVS markers, including the page-level marker. For example:
 * [["TTVS.VideoContent", 1200], ["TTVS", 1200]]
 * @returns The array of markers
 */
function getTtvsMarkers(): [string, number][] {
    return getMarkersWithType(ttvsPrefix, timeToVideoStart);
}

/**
 * Returns array of a provided markers based on markerName, optionally including the page-level marker.
 * @param markerPrefix the marker prefix for the markers
 * @param pageMarkerName the optional page-level markerName
 * @param includeTTVRWithRefreshDelay flag to include TTVR.WithRefreshDelay metric, which tracks TTVR + any refresh delay
 * @returns The array of markers
 */
function getMarkersWithType(markerPrefix: string, pageMarkerName?: string, includeTTVRWithRefreshDelay?: boolean) {
    const result: [string, number][] = [];
    let maxValue = 0;
    let maxValueNewTTVR = 0;
    primaryMarkers.forEach((value: number, key: string) => {
        if (key.startsWith(markerPrefix)) {
            result.push([key, value]);

            // Only include value in max TTVR when added to ttvrMarkers in setMarkers()
            if (value > maxValueNewTTVR && (!key.startsWith("TTVR") || ttvrMarkers.has(key))) {
                maxValueNewTTVR = value;
            }
            if (value > maxValue && (!key.startsWith("TTVR") || (ttvrMarkers.has(key) && ttvrOldMarkers.has(key)))) {
                maxValue = value;
            }

            // Find if markers need to be Aggregated
            const aggObj = aggregateMapper.get(key);
            if (aggObj?.name) {
                if (ttvrAggMarkers.get(aggObj.name) < value) {
                    ttvrAggMarkers.set(aggObj.name, value);
                }
                if (!aggObj.preserve) {
                    // Remove last entry since we only need the aggregate
                    result.pop();
                }
            }
        }
    });

    ttvrAggMarkers.forEach((value: number, key: string) => {
        if (key.startsWith(markerPrefix)) {
            result.push([key, value]);
        }
    });

    // Add the max, overall marker at the end
    if (pageMarkerName) {
        // Need to add 2 page level markers:
        // TTVR.New FY22 new calculation
        // TTVR prior calculation
        result.push([pageMarkerName, maxValue]);
        const newTtvrKey = timeToVisuallyReady + ".New";
        if (pageMarkerName === timeToVisuallyReady) {
            if (isNewTtvr) {
                result.push([newTtvrKey, maxValueNewTTVR]);
            } else {
                result.push([newTtvrKey, maxValue]);
            }
        }
    }

    // For TTVR with refresh delay, add the TTVR.WithRefreshDelay metric
    if (includeTTVRWithRefreshDelay && pageMarkerName === timeToVisuallyReady) {
        const pageTimings = getPageTimings();
        const refreshDelay: number = pageTimings["refreshDelay"] || 0;
        result.push(["TTVR.WithRefreshDelay", maxValue + refreshDelay]);
    }
    return result;
}

/**
 * Put marker names into a expanded/flattened array [ "TTVR.River", "TTVR.TopSites", "TTF.SearchBox" ]
 * @param markerNames The names of the markers to check. e.g. { TTVR: [ "River", "TopSites" ], TTF: [ "SearchBox" ] }
 * @param markerType The type of markers to return "TTVR", "TTF", or if undefined all
 */
function flattenMarkerNames(markerNames: MarkerNames, markerType: string): string[] {
    let ttvr: string[] = [];
    let ttpr: string[] = [];
    let ttf: string[] = [];

    if (markerNames && markerNames.TTVR && (!markerType || markerType === "TTVR")) {
        ttvr = markerNames.TTVR.map((n) => { return ttvrPrefix + n; });
    }

    if (markerNames && markerNames.TTF && (!markerType || markerType === "TTF")) {
        ttf = markerNames.TTF.map((n) => { return ttfPrefix + n; });
    }

    if (markerNames && markerNames.TTPR && markerType === "TTPR") {
        ttpr = markerNames.TTPR.map((n) => { return ttprPrefix + n; });
    }

    return [...ttvr, ...ttf, ...ttpr];
}

/**
 * helper method to get performance mark name.
 * @param markerName - performance mark name
 */
export function getSinglePerfMarkStartTimeByName(markerName: string): number {
    const perfMarks = window.performance.getEntriesByName(markerName);
    if (perfMarks && perfMarks[0] && perfMarks[0].startTime) {
        return perfMarks[0].startTime;
    }

    return -1;
}

/**
 * Reset the markers object singleton for test environments
 */
export function resetMarkersForTest(): void {
    primaryMarkers = new Map();
    secondaryMarkers = new Map();
    isSetMarkersCalled = false;
}

/**
 * Helper checks if the marker exist then calls markTtvr
 * @param name The marker name
 * @param markTimeNA To set -1 mark time for the TTVR to resolve the dependency
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function checkMarkTtvr(name = "", markTimeNA = false, time?: number): void {
    let marker = name;
    if (!marker.startsWith(ttvrPrefix)) {
        marker = ttvrPrefix+name;
    }
    if (!isSetMarkersCalled || !ttvrMarkers.has(marker) || name === "") {
        return;
    }
    markTtvr(name, markTimeNA, time);
}

/**
 * Helper checks if the marker exist then calls markTtvrRaf
 * @param name The marker name
 * @param markTimeNA To set -1 mark time for the TTVR to resolve the dependency
 * @param time - The elapsed time since window.performance.timing.navigationStart
 */
export function checkMarkTtvrRaf(name = "", markTimeNA = false, time?: number): void {
    let marker = name;
    if (!marker.startsWith(ttvrPrefix)) {
        marker = ttvrPrefix + name;
    }
    if (!isSetMarkersCalled || !ttvrMarkers.has(marker) || name === "") {
        return;
    }
    markTtvrRaf(name, markTimeNA, time);
}

/**
 * Set the flag to make the new TTVR calculation
 * @param newTtvr boolean to set new TTVR
 */
export function setIsNewTtvr(newTtvr = false): void {
    isNewTtvr = newTtvr;
}

/**
 * Register the markers and their aggregate marker name to be use as single value.
 * @param name Name of the Scalar Marker (TTVR.WeatherCardWV)
 * @param aggName Name of the Aggregate (TTVR.SDCards)
 * @param preserve flag to maintain scalar record scalar values or not
 */
export function addAggregateTtvrMarker(name = "", aggName = "", preserve = false) {
    let marker = name;
    let aggMarker = aggName;
    if (!isSetMarkersCalled || name === "" || aggName === ""|| !isNewTtvr) {
        return;
    }
    if (!marker.startsWith(ttvrPrefix)) {
        marker = ttvrPrefix + name;
    }
    if (!aggMarker.startsWith(ttvrPrefix)) {
        aggMarker = ttvrPrefix + aggName;
    }
    addTtvrMarker(marker);
    aggregateMapper.set(marker, {name: aggMarker, preserve: preserve});
    ttvrAggMarkers.set(aggMarker, -1);
}